import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminGet, adminPost } from './adminApi';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const mono = { fontFamily: "'IBM Plex Mono', monospace" };

export default function AdminAllocationPage() {
  const [params] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [types, setTypes] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);
  const [customerId, setCustomerId] = useState(params.get('customer') || '');
  const [siteId, setSiteId] = useState('');
  const [typeCode, setTypeCode] = useState('');
  const [stock, setStock] = useState([]);
  const [withCustomer, setWithCustomer] = useState([]);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminGet('/customers').then(cs => {
      setCustomers(cs);
      setCustomerId(prev => prev || cs[0]?.id || '');
    }).catch(e => toast.error(e.message));
    adminGet('/device-types').then(setTypes).catch(() => {});
  }, []);

  const loadStock = useCallback(() => {
    adminGet(`/allocation/available${typeCode ? `?device_type=${typeCode}` : ''}`).then(s => {
      setStock(s); setSelected({});
    }).catch(e => toast.error(e.message));
  }, [typeCode]);

  const loadCustomer = useCallback(() => {
    if (!customerId) return;
    adminGet(`/customers/${customerId}/org-units`).then(ous => {
      setOrgUnits(ous);
      const sites = ous.filter(o => o.active);
      setSiteId(prev => sites.find(s => s.id === prev) ? prev : (sites.find(s => s.is_site)?.id || sites[0]?.id || ''));
    }).catch(() => {});
    adminGet(`/customers/${customerId}/devices`).then(setWithCustomer).catch(() => {});
  }, [customerId]);

  useEffect(() => { loadStock(); }, [loadStock]);
  useEffect(() => { loadCustomer(); }, [loadCustomer]);

  const selectedIds = Object.keys(selected).filter(k => selected[k]);

  const allocate = async () => {
    if (!selectedIds.length || !siteId) { toast.error('Pick devices and a site'); return; }
    setBusy(true);
    try {
      const res = await adminPost(`/customers/${customerId}/allocations`, { device_ids: selectedIds, org_unit_id: siteId });
      toast.success(`Allocated ${res.allocated.length} devices to ${res.site}`);
      loadStock(); loadCustomer();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const recall = async (d) => {
    const reason = window.prompt('Recall reason:', 'Maintenance');
    if (reason === null) return;
    setBusy(true);
    try {
      await adminPost(`/devices/${d.id}/recall`, { reason });
      toast.success(`${d.trackotic_id} recalled — it will re-enter the QC queue`);
      loadStock(); loadCustomer();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const Sel = ({ label, value, onChange, children, testId }) => (
    <div className="flex-1 min-w-52">
      <label className="text-sm block mb-1" style={{ color: '#6b7069' }}>{label}</label>
      <select data-testid={testId} value={value} onChange={onChange}
        className="w-full h-10 px-3 rounded-md border bg-white text-sm" style={{ borderColor: '#D9D6CD' }}>
        {children}
      </select>
    </div>
  );

  const activeOrgUnits = useMemo(() => orgUnits.filter(o => o.active), [orgUnits]);

  return (
    <div className="p-8 max-w-7xl" data-testid="admin-allocation-page">
      <h1 className="text-4xl font-bold mb-6" style={{ color: '#1B1D1C' }}>Allocate devices</h1>

      <div className="rounded-lg border bg-white p-5 mb-5 flex gap-4 flex-wrap" style={{ borderColor: '#E5E3DC' }} data-testid="allocation-filters">
        <Sel label="Customer" value={customerId} onChange={e => setCustomerId(e.target.value)} testId="alloc-customer-select">
          {customers.map(c => <option key={c.id} value={c.id}>{c.legal_name}{c.vertical_code ? ` · ${c.vertical_code}` : ''}</option>)}
        </Sel>
        <Sel label="Site / hub" value={siteId} onChange={e => setSiteId(e.target.value)} testId="alloc-site-select">
          {activeOrgUnits.map(o => (
            <option key={o.id} value={o.id}>{o.is_site ? '📍 ' : ''}{o.name}{o.level_label ? ` (${o.level_label})` : ''}</option>
          ))}
        </Sel>
        <Sel label="Device type needed" value={typeCode} onChange={e => setTypeCode(e.target.value)} testId="alloc-type-select">
          <option value="">Any type</option>
          {types.map(t => <option key={t.code} value={t.code}>{t.display_name}</option>)}
        </Sel>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 items-start">
        <div className="rounded-lg border bg-white p-5" style={{ borderColor: '#E5E3DC' }} data-testid="available-stock-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold" style={{ color: '#1B1D1C' }}>Available stock</h2>
            <span className="text-xs" style={{ color: '#9a9e96' }}>QC passed · firmware approved</span>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
              <th className="py-2 w-8"></th><th className="font-medium">Track0tic ID</th>
              <th className="font-medium">Firmware</th><th className="font-medium">SIM valid till</th><th className="font-medium">Status</th></tr></thead>
            <tbody>
              {stock.map(d => (
                <tr key={d.id} className="border-b" style={{ borderColor: '#F3F1EA' }} data-testid={`stock-row-${d.trackotic_id}`}>
                  <td className="py-2.5">
                    <input type="checkbox" data-testid={`stock-check-${d.trackotic_id}`} checked={!!selected[d.id]}
                      onChange={e => setSelected({ ...selected, [d.id]: e.target.checked })}
                      className="w-4 h-4 accent-[#0B6B55]" />
                  </td>
                  <td style={mono}>{d.trackotic_id}</td>
                  <td style={mono}>{d.firmware || '—'}</td>
                  <td>{d.sim_valid_till ? new Date(d.sim_valid_till).toLocaleDateString('en-IN') : '[date]'}</td>
                  <td><span className="px-2 py-0.5 rounded text-xs" style={{ background: '#E7F2EE', color: '#0B6B55' }}>{d.lifecycle_status}</span></td>
                </tr>
              ))}
              {stock.length === 0 && <tr><td colSpan={5} className="py-6 text-center" style={{ color: '#9a9e96' }}>No allocatable stock</td></tr>}
            </tbody>
          </table>
          <div className="flex justify-end mt-4">
            <Button disabled={busy || !selectedIds.length} data-testid="allocate-devices-btn" onClick={allocate}
              className="text-white hover:opacity-90" style={{ background: '#0B6B55' }}>
              Allocate {selectedIds.length || ''} device{selectedIds.length === 1 ? '' : 's'} →
            </Button>
          </div>
        </div>

        <div className="rounded-lg border bg-white p-5" style={{ borderColor: '#E5E3DC' }} data-testid="with-customer-card">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#1B1D1C' }}>Already with this customer</h2>
          <table className="w-full text-sm">
            <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
              <th className="py-2 font-medium">Track0tic ID</th><th className="font-medium">Site</th>
              <th className="font-medium">Status</th><th></th></tr></thead>
            <tbody>
              {withCustomer.map(d => (
                <tr key={d.id} className="border-b" style={{ borderColor: '#F3F1EA' }} data-testid={`with-customer-row-${d.trackotic_id}`}>
                  <td className="py-2.5" style={mono}>{d.trackotic_id}</td>
                  <td>{d.site || '[Site]'}</td>
                  <td><span className="px-2 py-0.5 rounded text-xs" style={d.connectivity === 'Active'
                    ? { background: '#E7F2EE', color: '#0B6B55' } : { background: '#EFEDE6', color: '#6b7069' }}>{d.connectivity}</span></td>
                  <td className="text-right">
                    <Button size="sm" variant="outline" disabled={busy} data-testid={`recall-btn-${d.trackotic_id}`}
                      onClick={() => recall(d)} className="bg-white border-[#D9D6CD]">Recall</Button>
                  </td>
                </tr>
              ))}
              {withCustomer.length === 0 && <tr><td colSpan={4} className="py-6 text-center" style={{ color: '#9a9e96' }}>No devices with this customer</td></tr>}
            </tbody>
          </table>
          <p className="text-xs mt-4" style={{ color: '#9a9e96' }}>
            Allocation stamps the customer's tenant_id on the device; telemetry from then on lands in that customer's data. Recalled devices re-enter the QC queue.
          </p>
        </div>
      </div>
    </div>
  );
}
